// sales-page/src/components/OTPModal.jsx
import React, { useState, useEffect } from 'react';
import salesApi from '../core/salesApi';

const OTPModal = ({ isOpen, onClose, onVerified, promoterCode, visitorFingerprint }) => {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email) {
      setError('Please enter your email');
      setLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      await salesApi.sendOTP({
        email,
        promoter_code: promoterCode,
        visitor_fingerprint: visitorFingerprint
      });
      setStep('verify');
      setResendCooldown(60);
    } catch (err) {
      const data = err.response?.data;
      if (data?.already_registered) {
        setError('This email already has a TrafikTrak account. Please use a different email address.');
      } else {
        setError(data?.message || 'Failed to send OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!otpCode || otpCode.length !== 6) {
      setError('Please enter the 6-digit code');
      setLoading(false);
      return;
    }

    try {
      await salesApi.verifyOTP({
        email,
        otp_code: otpCode,
        promoter_code: promoterCode
      });
      
      salesApi.storeEmailVerification(email, true);
      setSuccess(true);
      
      setTimeout(() => {
        onVerified(email);
        onClose();
        document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    setError('');
    setLoading(true);

    try {
      await salesApi.resendOTP({
        email,
        promoter_code: promoterCode,
        visitor_fingerprint: visitorFingerprint
      });
      setResendCooldown(60);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    border: '1.5px solid #111111',
    borderRadius: '4px',
    fontFamily: 'inherit',
    fontSize: '16px',
    color: '#111111',
    background: '#FFFFFF'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: 700,
    color: '#111111',
    marginBottom: '8px'
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="max-w-md w-full relative"
        style={{ background: '#FFFFFF', border: '1.5px solid #111111', borderRadius: '6px', boxShadow: '0 8px 24px rgba(0,0,0,0.25)', padding: '32px', fontFamily: "'Arial', sans-serif" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{ position: 'absolute', top: '18px', right: '18px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#666666', lineHeight: 1 }}
          aria-label="Close"
        >
          &times;
        </button>

        {success ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div className="checklist-item" style={{ justifyContent: 'center', marginBottom: '14px' }}>
              <span className="check-icon">&#9989;</span>
              <div style={{ fontSize: '22px', fontWeight: 900, color: '#111111' }}>Email Verified!</div>
            </div>
            <p style={{ color: '#434343', fontSize: '16px' }}>Choose your plan below to complete your purchase.</p>
          </div>
        ) : step === 'email' ? (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '22px' }}>
              <h3 style={{ fontSize: '26px', fontWeight: 900, color: '#111111', marginBottom: '8px' }}>Verify Your Email</h3>
              <p style={{ color: '#434343', fontSize: '16px' }}>We'll send you a verification code to continue</p>
            </div>

            {error && (
              <div style={{ marginBottom: '16px', padding: '10px 14px', border: '1.5px solid #DC2626', background: '#FEF2F2', borderRadius: '4px' }}>
                <p style={{ fontSize: '14px', color: '#B91C1C', margin: 0 }}>{error}</p>
              </div>
            )}

            <form onSubmit={handleSendOTP}>
              <div style={{ marginBottom: '18px' }}>
                <label htmlFor="email" style={labelStyle}>
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={inputStyle}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="doc-cta-button"
                style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                {loading ? 'Sending...' : 'Send Verification Code'}
              </button>
            </form>
          </div>
        ) : (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '22px' }}>
              <h3 style={{ fontSize: '26px', fontWeight: 900, color: '#111111', marginBottom: '8px' }}>Enter Verification Code</h3>
              <p style={{ color: '#434343', fontSize: '16px', marginBottom: '2px' }}>We sent a 6-digit code to</p>
              <p style={{ fontWeight: 700, color: '#111111' }}>{email}</p>
            </div>

            {error && (
              <div style={{ marginBottom: '16px', padding: '10px 14px', border: '1.5px solid #DC2626', background: '#FEF2F2', borderRadius: '4px' }}>
                <p style={{ fontSize: '14px', color: '#B91C1C', margin: 0 }}>{error}</p>
              </div>
            )}

            <form onSubmit={handleVerifyOTP}>
              <div style={{ marginBottom: '18px' }}>
                <label htmlFor="otp" style={labelStyle}>
                  Verification Code
                </label>
                <input
                  type="text"
                  id="otp"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  style={{ ...inputStyle, textAlign: 'center', fontSize: '26px', letterSpacing: '6px' }}
                  placeholder="000000"
                  maxLength={6}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="doc-cta-button"
                style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </button>
            </form>

            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <p style={{ fontSize: '14px', color: '#434343', margin: 0 }}>
                Didn't receive the code?{' '}
                {resendCooldown > 0 ? (
                  <span style={{ color: '#999999' }}>Resend in {resendCooldown}s</span>
                ) : (
                  <button
                    onClick={handleResendOTP}
                    disabled={loading}
                    style={{ background: 'none', border: 'none', color: '#1D4ED8', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: '14px', padding: 0 }}
                  >
                    Resend Code
                  </button>
                )}
              </p>
              <button
                onClick={() => {
                  setStep('email');
                  setOtpCode('');
                  setError('');
                }}
                style={{ marginTop: '8px', background: 'none', border: 'none', color: '#666666', textDecoration: 'underline', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Change Email Address
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OTPModal;